"use client";

import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { EmptyState } from "../../feedback/empty-state";
import { Button } from "../../inputs/button";

export interface TabularEditorErrorBoundaryProps {
  children: React.ReactNode;
  /** Override the fallback. Default renders an `EmptyState`. */
  fallback?: React.ReactNode;
  onReset?: () => void;
}

interface TabularEditorErrorBoundaryState {
  error: Error | null;
}

/**
 * Minimal React error boundary tailored for grid surfaces. Captures
 * uncaught render-phase errors in the body and shows a recoverable
 * fallback. Exercised by `grid-table.stories.tsx` (`ErrorBoundaryFallback`).
 */
export class TabularEditorErrorBoundary extends React.Component<
  TabularEditorErrorBoundaryProps,
  TabularEditorErrorBoundaryState
> {
  state: TabularEditorErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): TabularEditorErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Logged for Sentry / app-shell boundary; intentionally silent in storybook.
    if (typeof console !== "undefined" && console.error) {
      console.error("[TabularEditorErrorBoundary]", error, info.componentStack);
    }
  }

  private handleReset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <EmptyState
          icon={<AlertTriangle />}
          title="The grid failed to render"
          action={
            <Button variant="outline" size="sm" onClick={this.handleReset}>
              Try again
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}
